/**
 * The review sheet's wiring (import-hub.tsx ReviewSheet, F4), kept apart from its hooks so the order of its calls is
 * tested without a browser: Approve and bind approves, then saves the reviewer's defect words, then closes; a refused
 * approval saves no word and keeps the sheet; once approved, words that failed are said in the sheet and tried again
 * on request, the approval standing. The sheet hands in its mutations, its toast and its close.
 */
import type { VocabBindingInput } from "@/lib/api/twin-vocabulary";
import {
  PACK_REFUSAL,
  ROLE_REFUSAL,
  approveWithWords,
  approvedText,
  plural,
  saveWords,
  wordsNotSavedText,
} from "./review";

/** Where the sheet stands. Once the version is approved and its words failed, only the words can still be saved. */
export type ReviewPhase =
  | { kind: "idle" }
  | { kind: "approving" }
  | { kind: "sending_back" }
  | { kind: "words_failed"; message: string }
  | { kind: "retrying"; message: string }
  | { kind: "closed" };

export interface ReviewFlowIO {
  approve: () => Promise<unknown>;
  putWords: (bindings: VocabBindingInput[]) => Promise<unknown>;
  setPhase: (phase: ReviewPhase) => void;
  toast: (tone: "ok" | "error" | "warn", text: string) => void;
  close: () => void;
  /** The approval's failure, in words. */
  approveError: (err: unknown) => string;
}

export interface ReviewFlowInput {
  label: string;
  version: number;
  /** The bindings the reviewer's choices save. */
  words: VocabBindingInput[];
  /** Why the words cannot be saved now (wordsGate), so none is sent; "" or absent when they can. */
  blocked?: string;
}

/** Whether the words can go with the approval: what the sheet knows before it sends a PUT the server would refuse. */
export interface WordsGateInput {
  /** The words the draft proposes. */
  proposed: number;
  /** The org's bindings, which a word's decision starts from. */
  bindings: "loading" | "ready" | "failed";
  /** The reviewer's role may write the org's model (model.write). */
  canWrite: boolean;
  /** The org has the foundry pack, whose routes the vocabulary is; "unknown" when that did not load. */
  foundry: "loading" | "yes" | "no" | "unknown";
}

export interface WordsGate {
  /** Approve waits: the org's decided words (or its packs) are still being read. */
  waiting: boolean;
  /** Why no word is sent with this approval, or "". */
  blocked: string;
  /** What the words section says first, or "". */
  notice: string;
}

/**
 * Whether the words go with the approval. Approve waits until the org's bindings are read, since a word's choice
 * starts from the org's decision on it; when they did not load no word is sent. No word is sent either for a role
 * without model write, or an org without the foundry pack: the server would refuse the PUT. When the packs did not
 * load, the server decides. A draft that proposes no word asks nothing.
 */
export function wordsGate(i: WordsGateInput): WordsGate {
  const none = { waiting: false, blocked: "", notice: "" };
  if (i.proposed === 0) return none;
  if (i.bindings === "loading" || i.foundry === "loading") {
    return { waiting: true, blocked: "", notice: "Reading the org's decided words before approving…" };
  }
  const noneSaved = "approving puts the reader in use, and saves none of its words.";
  if (i.bindings === "failed") {
    return {
      waiting: false,
      blocked: "the org's decided words did not load, so none are saved with this approval",
      notice: `The org's decided words did not load: ${noneSaved}`,
    };
  }
  if (!i.canWrite) {
    return { waiting: false, blocked: ROLE_REFUSAL, notice: `Your role in this org may not save words (that takes model write): ${noneSaved}` };
  }
  if (i.foundry === "no") {
    return { waiting: false, blocked: PACK_REFUSAL, notice: `This org has no foundry pack: ${noneSaved}` };
  }
  return none;
}

/** Approve and bind: the approval, then the words, then the sheet closes; a failure of either is said, and nothing
 *  withdraws an approval. Words that cannot be saved (input.blocked) are not sent, and the toast says why. */
export async function runApprove(io: ReviewFlowIO, input: ReviewFlowInput): Promise<void> {
  io.setPhase({ kind: "approving" });
  const blocked = input.blocked && input.words.length > 0 ? input.blocked : "";
  const out = await approveWithWords({ approve: io.approve, putWords: io.putWords }, blocked ? [] : input.words);
  if (!out.approved) {
    io.setPhase({ kind: "idle" });
    io.toast("error", io.approveError(out.error));
    return;
  }
  if (blocked) {
    io.toast("warn", wordsNotSavedText(input.label, input.version, blocked));
    io.setPhase({ kind: "closed" });
    io.close();
    return;
  }
  if (!out.words.ok) {
    io.setPhase({ kind: "words_failed", message: wordsNotSavedText(input.label, input.version, out.words.message) });
    return;
  }
  io.toast("ok", approvedText(input.label, input.version, out.words.saved));
  io.setPhase({ kind: "closed" });
  io.close();
}

/** Save the words again, once the version is approved and they failed. */
export async function runRetryWords(io: ReviewFlowIO, input: ReviewFlowInput, message: string): Promise<void> {
  io.setPhase({ kind: "retrying", message });
  const res = await saveWords(io.putWords, input.words);
  if (!res.ok) {
    io.setPhase({ kind: "words_failed", message: wordsNotSavedText(input.label, input.version, res.message) });
    return;
  }
  io.toast("ok", res.saved ? `${plural(res.saved, "defect word")} saved.` : "No defect word was left to save.");
  io.setPhase({ kind: "closed" });
  io.close();
}

/** What the footer's buttons allow in a phase: approve only when idle, every equipment key bound and the sheet not
 *  waiting for what it reads first; send back only when idle and with a note. */
export function footerOf(
  phase: ReviewPhase,
  ctx: { missing: number; note: string; waiting: boolean; canSendBack?: boolean },
): { approveDisabled: boolean; rejectDisabled: boolean; busy: boolean } {
  const idle = phase.kind === "idle";
  // A send-back says why: the note, or (plan v21 B5) a correction to one of the draft's assumptions.
  const canSendBack = ctx.canSendBack ?? ctx.note.trim() !== "";
  return {
    approveDisabled: !idle || ctx.missing > 0 || ctx.waiting,
    rejectDisabled: !idle || !canSendBack,
    busy: phase.kind === "approving" || phase.kind === "retrying",
  };
}
